import { api } from '../../api';
import { DateGroupType } from '../../model/active/dateGroupType';
import { PassiveParameterTypeEnum } from '../../model/passive/enum/passiveParameterTypeEnum';
import { convertDate } from '../../helpers/utilityFunctions/utilityFunctions';

export const passiveParametersService = {
  getDailyParametersDatesByRange,
  getDailyParametersByDate,
  getDailyParameterDatesByRange,
  getPassiveParameterDataByType,
  // Keep for backward compatibility
  getAverageWalkingSpeed,
};

function getDailyParametersDatesByRange(
  patientId: string,
  startDate: string,
  endDate: string
) {
  return api.get(`/passive/dates/${patientId}/${startDate}/${endDate}`);
}

// This needs /dailymetrics because the function exists on a different BE controller
// - may be refactored at some point
function getDailyParametersByDate(accountGuid: string, date: Date) {
  // Format the date as YYYY-MM-DD; interpolating a Date directly yields its
  // locale string (e.g. "Thu Oct 15 2026 ... GMT+0200"), which the API rejects.
  return api.get(
    `/dailymetrics/date?accountGuid=${accountGuid}&date=${convertDate(date)}`
  );
}

function getDailyParameterDatesByRange(
  accountGuid: string,
  dateStart: string,
  dateEnd: string,
  type: PassiveParameterTypeEnum = PassiveParameterTypeEnum.AverageWalkingSpeed
) {
  // Map enum value to string for API
  const typeString = getPassiveParameterTypeString(type);

  return api.get(
    `/passive/dates/${accountGuid}/${dateStart}/${dateEnd}?type=${typeString}`
  );
}

function getPassiveParameterDataByType(
  accountGuid: string,
  dateStart: Date,
  dateEnd: Date,
  dateType: DateGroupType,
  parameterType: PassiveParameterTypeEnum
) {
  // Use convertDate utility function to format dates as YYYY-MM-DD
  const start = convertDate(new Date(dateStart));

  // If start and end dates are the same, add 1 day to end date
  // This is because the API uses a half-open interval: StartDateTime <= date < EndDateTime
  let adjustedEndDate = new Date(dateEnd);
  if (start === convertDate(adjustedEndDate)) {
    adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
  }
  const end = convertDate(adjustedEndDate);

  // Map enum value to string for API
  const typeString = getPassiveParameterTypeString(parameterType);

  return api.get(
    `/dailymetrics/by-date-range/?accountGuid=${accountGuid}&type=${typeString}&dateStart=${start}&dateEnd=${end}`
  );
}

// Keep for backward compatibility
function getAverageWalkingSpeed(
  accountGuid: string,
  dateStart: Date,
  dateEnd: Date,
  dateType: DateGroupType
) {
  return getPassiveParameterDataByType(
    accountGuid,
    dateStart,
    dateEnd,
    dateType,
    PassiveParameterTypeEnum.AverageWalkingSpeed
  );
}

// Helper function to map enum values to API string values
function getPassiveParameterTypeString(type: PassiveParameterTypeEnum): string {
  switch (type) {
    case PassiveParameterTypeEnum.AverageWalkingSpeed:
      return 'AverageWalkingSpeed';
    case PassiveParameterTypeEnum.Temperature:
      return 'Temperature';
    case PassiveParameterTypeEnum.AverageStrideLength:
      return 'AverageStrideLength';
    case PassiveParameterTypeEnum.AverageAngle:
      return 'AverageAngle';
    case PassiveParameterTypeEnum.TotalDistance:
      return 'TotalDistance';
    case PassiveParameterTypeEnum.AverageAsymmetry:
      return 'AverageAsymmetry';
    case PassiveParameterTypeEnum.AverageCadence:
      return 'AverageCadence';
    case PassiveParameterTypeEnum.AverageVariability:
      return 'AverageVariability';
    case PassiveParameterTypeEnum.AverageSwing:
      return 'AverageSwing';
    case PassiveParameterTypeEnum.AverageStance:
      return 'AverageStance';
    case PassiveParameterTypeEnum.AverageCycleDuration:
      return 'AverageCycleDuration';
    case PassiveParameterTypeEnum.AverageSwingSpeed:
      return 'AverageSwingSpeed';
    case PassiveParameterTypeEnum.AveragePeakAngleVelocity:
      return 'AveragePeakAngleVelocity';
    case PassiveParameterTypeEnum.TotalSteps:
      return 'TotalSteps';
    case PassiveParameterTypeEnum.AverageDoubleSupport:
      return 'AverageDoubleSupport';
    case PassiveParameterTypeEnum.InsoleUsage:
      return 'InsoleUsage';
    case PassiveParameterTypeEnum.FallRiskIndex:
      return 'FallRiskIndex';
    case PassiveParameterTypeEnum.Latitude:
      return 'Latitude';
    case PassiveParameterTypeEnum.Longitude:
      return 'Longitude';
    case PassiveParameterTypeEnum.BatteryLevel:
      return 'BatteryLevel';
    case PassiveParameterTypeEnum.Compliance:
      return 'Compliance';
    default:
      return 'AverageWalkingSpeed';
  }
}
